import * as XLSX from 'xlsx';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export async function DownloadExcelAnalytics(deliveries, selectedDate, userId) {
  const dateStr = selectedDate.replace(/-/g, '');

  // ── Sheet 1: Regular deliveries ─────────────────────────────
  const headers = [
    "ID", "Destinatario", "Teléfono", "Dirección", "Distrito",
    "Producto", "Estado", "Por Cobrar", "Cobrado",
    "Fecha Entrega", "Hora Llegada", "Notas", "Notas Conductor"
  ];

  const rows = deliveries.map(delivery => {
    const totalCobrado = parseFloat(delivery.charged || 0) + parseFloat(delivery.fastmax_transfer_amount || 0);
    return [
      delivery.id || '',
      delivery.recipient_name || '',
      delivery.recipient_phone || '',
      delivery.address || '',
      delivery.district || '',
      delivery.product || '',
      delivery.status || '',
      delivery.charge ? `S/ ${parseFloat(delivery.charge).toFixed(2)}` : '',
      totalCobrado > 0 ? `S/ ${totalCobrado.toFixed(2)}` : '',
      delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString('es-PE') : '',
      delivery.arrival_time ? new Date(delivery.arrival_time).toLocaleString('es-PE') : '',
      delivery.notes || '',
      delivery.notes_driver || ''
    ];
  });

  // ── Postergados section
  const postponedHeaders = [
    "ID", "Destinatario", "Teléfono", "Dirección", "Distrito",
    "Producto", "Estado", "Tienda", "Motivo Postergación", "Postergado Desde", "Postergado Para"
  ];

  let postponedRows = [];
  let statusHistory = [];
  try {
    const res = await axios.get(`${API_URL}/api/delivery-points/postponed`, {
      params: {
        date: selectedDate,
        user_id: userId,
      },
    });
    const postponed = Array.isArray(res.data) ? res.data : (res.data.deliveries ?? []);
    postponedRows = postponed.map(d => [
      d.id || '',
      d.recipient_name || '',
      d.recipient_phone || '',
      d.address || '',
      d.district || '',
      d.product || '',
      d.status || '',
      d.username || '',
      d.postponed_reason || '',
      formatDate(d.selected_postponed_from_date || d.postponed_from_date),
      formatDate(d.selected_postponed_to_date || d.current_delivery_date)
    ]);
  } catch (err) {
    console.error('Error fetching postponed deliveries for Excel:', err);
  }

  try {
    const res = await axios.get(`${API_URL}/api/delivery-points/status-history`, {
      params: {
        date: selectedDate,
        user_id: userId,
      },
    });
    statusHistory = Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error('Error fetching delivery status history for Excel:', err);
  }

  // ── Build single sheet: entregas + blank row + postergados ──
  const blankRow = ['']; // blank separator row
  const postergadosTitleRow = ['── POSTERGADOS ──'];

  const allData = [
    headers,
    ...rows,
    blankRow,
    postergadosTitleRow,
    postponedHeaders,
    ...postponedRows,
  ];

  const ws1 = XLSX.utils.aoa_to_sheet(allData);
  ws1['!cols'] = [
    { wch: 8 }, { wch: 25 }, { wch: 15 }, { wch: 40 }, { wch: 20 },
    { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    { wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 15 }
  ];

  // ── Build workbook ──────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, 'Entregas');

  const postponedHistory = statusHistory.filter(log => log.to_status === 'postergado');
  if (postponedHistory.length > 0) {
    const wsPostponedHistory = XLSX.utils.aoa_to_sheet([
      [
        'ID', 'Destinatario', 'Telefono', 'Tienda', 'Estado Anterior',
        'Estado Nuevo', 'Fecha Original', 'Nueva Fecha', 'Motivo',
        'Fecha del Cambio', 'Origen'
      ],
      ...postponedHistory.map(log => [
        log.delivery_point_id || '',
        log.recipient_name || '',
        log.recipient_phone || '',
        log.username || '',
        getStatusLabel(log.from_status),
        getStatusLabel(log.to_status),
        formatDate(log.from_delivery_date),
        formatDate(log.to_delivery_date),
        log.reason || log.note || '',
        formatDateTime(log.created_at),
        getSourceLabel(log.source, log.actor_role),
      ]),
    ]);
    wsPostponedHistory['!cols'] = [
      { wch: 8 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 16 },
      { wch: 16 }, { wch: 15 }, { wch: 15 }, { wch: 40 },
      { wch: 22 }, { wch: 18 }
    ];
    XLSX.utils.book_append_sheet(wb, wsPostponedHistory, 'Postergados Historial');
  }

  if (statusHistory.length > 0) {
    const wsStatusHistory = XLSX.utils.aoa_to_sheet([
      [
        'ID', 'Destinatario', 'Telefono', 'Tienda', 'Estado Anterior',
        'Estado Nuevo', 'Fecha Original', 'Fecha Nueva/Actual',
        'Motivo/Nota', 'Fecha del Cambio', 'Origen', 'Estado Actual'
      ],
      ...statusHistory.map(log => [
        log.delivery_point_id || '',
        log.recipient_name || '',
        log.recipient_phone || '',
        log.username || '',
        getStatusLabel(log.from_status),
        getStatusLabel(log.to_status),
        formatDate(log.from_delivery_date),
        formatDate(log.to_delivery_date),
        log.reason || log.note || '',
        formatDateTime(log.created_at),
        getSourceLabel(log.source, log.actor_role),
        getStatusLabel(log.current_status),
      ]),
    ]);
    wsStatusHistory['!cols'] = [
      { wch: 8 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 16 },
      { wch: 16 }, { wch: 15 }, { wch: 18 }, { wch: 40 },
      { wch: 22 }, { wch: 18 }, { wch: 16 }
    ];
    XLSX.utils.book_append_sheet(wb, wsStatusHistory, 'Historial Estados');
  }

  XLSX.writeFile(wb, `Entregas_${dateStr}.xlsx`);
}

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('es-PE');
}

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('es-PE');
}

function getStatusLabel(status) {
  const labels = {
    pending: 'Pendiente',
    encamino: 'En Camino',
    in_transit: 'En Camino',
    entregado: 'Entregado',
    completed: 'Completado',
    cancelado: 'Cancelado',
    rechazado: 'Rechazado',
    postergado: 'Postergado',
  };
  return labels[status] || status || '';
}

function getSourceLabel(source, actorRole) {
  const labels = {
    admin_postpone: 'Admin',
    admin_single_status: 'Admin',
    admin_bulk_status: 'Admin',
    admin_delivery_point_edit: 'Admin',
    admin_delivery_point_upsert: 'Admin',
    admin_update_sequences: 'Admin',
    driver_submit: 'Driver',
    driver_start_route: 'Driver',
    driver_route_eta: 'Driver',
    delivery_point_patch: 'Usuario',
    backfill: 'Sistema',
  };
  return labels[source] || actorRole || source || '';
}

export function DownloadExcelProvinciaAnalytics(deliveries, selectedDate) {
  const dateStr = selectedDate.replace(/-/g, '');

  const headers = [
    "ID", "Destinatario", "DNI", "Teléfono", "Provincia", "Distrito",
    "Dirección", "Agencia", "Código Recojo", "Producto", "Estado",
    "Por Cobrar", "Anticipo", "Fecha Entrega", "Notas", "Comprobantes"
  ];

  const rows = deliveries.map(delivery => [
    delivery.id || '',
    delivery.recipient_name || '',
    delivery.recipient_dni || '',
    delivery.recipient_phone || '',
    delivery.province || '',
    delivery.district || '',
    delivery.address || '',
    delivery.agency_address || '',
    delivery.agency_pickup_code || '',
    delivery.product || '',
    delivery.status || '',
    delivery.charge ? `S/ ${parseFloat(delivery.charge).toFixed(2)}` : '',
    delivery.deposit_advance ? `S/ ${parseFloat(delivery.deposit_advance).toFixed(2)}` : '',
    delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString('es-PE') : '',
    delivery.notes || '',
    delivery.images && delivery.images.length > 0
      ? `${delivery.images.length} imagen(es)`
      : 'Sin imágenes'
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = [
    { wch: 8 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
    { wch: 40 }, { wch: 35 }, { wch: 15 }, { wch: 30 }, { wch: 12 },
    { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 15 }
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Entregas Provincia');

  XLSX.writeFile(wb, `Entregas_Provincia_${dateStr}.xlsx`);
}
